import type { GitpodAPI } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { getPrincipal } from "@/principal";
import { getAccountQueryKey, useGetAccount } from "@/queries/account-queries";
import { editorQueryKey } from "@/queries/editor-queries";
import { defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { getMemberLoader } from "@/queries/member-loader";
import { autoPaginate, parsePaginationToken } from "@/queries/pagination";
import { keyWithPrincipal } from "@/queries/principal-key";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { TAX_ID_TYPES_BY_ID } from "@/routes/settings/organization/billing/stripe-tax-ids";
import { toPlainMessage, type PartialMessage, type PlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import type { PaymentIntent } from "@stripe/stripe-js";
import {
    keepPreviousData,
    queryOptions,
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type QueryClient,
} from "@tanstack/react-query";
import {
    GetBillingInfoResponse,
    SubscriptionType,
    type AutoTopupSettings,
    type BillingAddress,
    type CancelSubscriptionResponse,
    type CompleteBillingSetupResponse,
    type GetCouponResponse,
    type GetStripePortalUrlResponse,
    type SetupBillingResponse,
} from "gitpod-next-api/gitpod/v1/billing_pb";
import { ResourceOperation, ResourceType, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import {
    CreateSSOConfigurationRequest,
    DeleteDomainVerificationRequest,
    DeleteSSOConfigurationRequest,
    InviteDomains,
    OrganizationRole,
    UpdateSSOConfigurationRequest,
    type CustomDomain,
    type CustomDomainProvider,
    type DomainVerification,
    type ListMembersRequest_Filter,
    type ListMembersRequest_Sort,
    type Organization,
    type OrganizationMember,
    type OrganizationPolicies,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { UserStatus } from "gitpod-next-api/gitpod/v1/user_pb";
import { useCallback, useEffect, useMemo, useRef } from "react";

export type PlainOrganization = PlainMessage<Organization>;

export const organizationQueryKey = {
    get: (id?: string) => keyWithPrincipal(["organization", { id }]),
    orgInvite: (organizationId: string) => keyWithPrincipal(["org-invite", { organizationId }]),
    listSSOConfigurations: (organizationId: string) => keyWithPrincipal(["sso-configs", "list", { organizationId }]),
    listMembers: (
        organizationId: string,
        filter?: PartialMessage<ListMembersRequest_Filter>,
        sort?: PartialMessage<ListMembersRequest_Sort>,
    ) => keyWithPrincipal(["members", "list", { organizationId, filter, sort }]),
    listMembersPaginated: (
        organizationId: string,
        page?: string,
        filter?: PartialMessage<ListMembersRequest_Filter>,
        sort?: PartialMessage<ListMembersRequest_Sort>,
    ) => keyWithPrincipal(["members", "list", "paginated", { organizationId, filter, sort, page }]),
    listMembersInfinite: (organizationId: string, search?: string, excludeGroupIds?: string[]) =>
        keyWithPrincipal(["members", "list", "infinite", { organizationId, search, excludeGroupIds }]),
    listMembersByIds: (organizationId: string, userIds: string[]) =>
        keyWithPrincipal(["members", "list", "byIds", { organizationId, userIds }]),
    member: (userId: string) => keyWithPrincipal(["member", userId]),
    getDomainVerification: (organizationId: string, domain?: string) =>
        keyWithPrincipal(["domain-verification", "get", { organizationId, domain }]),
    listDomainVerifications: (organizationId: string) =>
        keyWithPrincipal(["domain-verifications", "list", { organizationId }]),
    getOrganizationPolicies: (organizationId: string) =>
        keyWithPrincipal(["organization-policies", "get", { organizationId }]),
    getBillingInfo: (organizationId: string) => keyWithPrincipal(["billing-info", { organizationId }]),
    listSubscriptions: (organizationId: string) => keyWithPrincipal(["subscriptions", "list", { organizationId }]),
    getCustomDomain: (organizationId: string) => keyWithPrincipal(["custom-domain", "get", { organizationId }]),
    getEnforceDomainPolicy: (organizationId: string) =>
        keyWithPrincipal(["enforce-domain-policy", "get", { organizationId }]),
    getAnnouncementBanner: (organizationId: string) =>
        keyWithPrincipal(["announcement-banner", "get", { organizationId }]),
    getAutoTopupSettings: (organizationId: string) =>
        keyWithPrincipal(["auto-topup-settings", "get", { organizationId }]),
};

const toPlainOrganization = (organization: Organization): PlainOrganization => {
    return toPlainMessage(organization);
};

export type PlainOrganizationMember = PlainMessage<OrganizationMember>;

export const toPlainOrganizationMember = (member: OrganizationMember): PlainOrganizationMember => {
    return toPlainMessage(member);
};

export const useOrganization = () => {
    const api = useGitpodAPI();
    const { data: user, refetch: refetchUser } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.get(user?.organizationId),
        queryFn: async () => {
            let organizationId = user?.organizationId;
            let userId = user?.id;

            // if the principal doesn't match the user, refetch the user
            if (getPrincipal() !== userId) {
                const newUser = await refetchUser();
                userId = newUser?.data?.id;
                organizationId = newUser?.data?.organizationId;
                if (getPrincipal() !== userId) {
                    throw new Error("Principal does not match user");
                }
            }
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }
            const { organization } = await api.organizationService.getOrganization({
                organizationId,
            });

            if (!organization) {
                throw new Error("Organization not found");
            }
            return toPlainMessage(organization);
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user?.organizationId && !!user?.id && getPrincipal() === user?.id,
    });
};

export const useOrganizationInviteSummary = (inviteID?: string) => {
    const api = useGitpodAPI();

    const query = useQuery({
        queryKey: keyWithPrincipal(["invite-summary", { inviteID: inviteID }]),
        queryFn: async () => {
            const resp = await api.organizationService.getOrganizationInviteSummary({
                inviteId: inviteID,
            });

            return { ...resp };
        },
        staleTime: 10000,
        retry: false,
        enabled: !!inviteID,
    });

    return { ...query, isLoading: query.isLoading || query.isFetching || query.isPending };
};

export const useCreateOrganization = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: account } = useGetAccount();

    return useMutation({
        mutationFn: async ({
            name,
            inviteAccountsWithMatchingDomain,
        }: {
            name: string;
            inviteAccountsWithMatchingDomain: boolean;
        }) => {
            if (!account) {
                throw new Error("Not authenticated");
            }

            const { organization, member } = await api.organizationService.createOrganization({
                name,
                joinOrganization: true,
                inviteAccountsWithMatchingDomain,
            });

            if (!organization) {
                throw new Error("Failed to create organization");
            }
            if (!member) {
                throw new Error("Failed to join organization");
            }

            return {
                organization: toPlainOrganization(organization),
                member: toPlainOrganizationMember(member),
            };
        },
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: getAccountQueryKey() });
        },
        onError: (error) => {
            console.error(error);
        },
    });
};

export const useJoinOrganization = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: account } = useGetAccount();

    return useMutation({
        mutationFn: async ({ organizationID }: { organizationID: string }) => {
            if (!account) {
                throw new Error("Not authenticated");
            }

            const { member } = await api.organizationService.joinOrganization({
                joinId: {
                    value: organizationID,
                    case: "organizationId",
                },
            });

            if (!member) {
                throw new Error("Failed to join organization");
            }

            return {
                member: toPlainOrganizationMember(member),
            };
        },
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: getAccountQueryKey() });
        },
        onError: (error) => {
            console.error(error);
        },
    });
};

export const useLeaveOrganization = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: account } = useGetAccount();

    return useMutation({
        mutationFn: async ({ userId }: { userId: string }) => {
            if (!account) {
                throw new Error("Not authenticated");
            }

            return await api.organizationService.leaveOrganization({
                userId,
            });
        },
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: getAccountQueryKey() });
        },
        onError: (error) => {
            console.error(error);
        },
    });
};

export const useUpdateOrganization = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ name, inviteDomains }: { name?: string; inviteDomains?: string[] }) => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            let inviteDomainsMessage: InviteDomains | undefined = undefined;
            if (inviteDomains !== undefined) {
                inviteDomainsMessage = new InviteDomains();
                inviteDomainsMessage.domains = inviteDomains;
            }

            const organizationId = user.organizationId;
            const { organization } = await api.organizationService.updateOrganization({
                organizationId,
                name,
                inviteDomains: inviteDomainsMessage,
            });

            if (!organization) {
                throw new Error("Failed to update organization");
            }

            return {
                organization: toPlainOrganization(organization),
            };
        },
        onSuccess: async () => {
            await client.invalidateQueries({
                queryKey: organizationQueryKey.get(user?.organizationId),
            });
            // to also update the memberships (org switcher!)
            await client.invalidateQueries({ queryKey: getAccountQueryKey() });
        },
        onError: (error) => {
            console.error(error);
        },
    });
};

const MEMBERS_PAGE_SIZE = 25;

type MembersPageData = {
    members: PlainOrganizationMember[];
    totalCount: number | undefined;
    totalPages: number | undefined;
    pageSize: number;
    nextToken: string | undefined;
};

type UseMembersPageParams = {
    enabled?: boolean;
    filter?: PartialMessage<ListMembersRequest_Filter>;
    sort?: PartialMessage<ListMembersRequest_Sort>;
    page?: number;
};

/**
 * Returns query options for fetching a single page of members.
 * Used by useMembersPage and for prefetching.
 */
export const useGetMembersPageOptions = ({ enabled = true, filter, sort, page = 0 }: UseMembersPageParams = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const queryKey = organizationQueryKey.listMembersPaginated(user?.organizationId || "", String(page), filter, sort);

    return queryOptions({
        queryKey,
        queryFn: async (): Promise<MembersPageData> => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const token = page === 0 ? "" : String(page);
            const response = await api.organizationService.listMembers({
                organizationId: user?.organizationId,
                filter,
                sort,
                pagination: {
                    pageSize: MEMBERS_PAGE_SIZE,
                    token,
                },
            });

            const members = response.members.map(toPlainOrganizationMember);
            const pagination = parsePaginationToken(response.pagination?.nextToken);

            // Extract metadata from pagination token, or infer from response
            let totalPages: number | undefined;
            let totalCount: number | undefined;

            if (pagination && pagination.totalPages !== -1) {
                totalPages = pagination.totalPages;
                totalCount = pagination.totalCount !== -1 ? pagination.totalCount : undefined;
            } else if (page === 0 && !response.pagination?.nextToken) {
                // No nextToken on page 0 means all data fits on one page
                totalPages = 1;
                totalCount = members.length;
            }

            return {
                members,
                totalCount,
                totalPages,
                pageSize: MEMBERS_PAGE_SIZE,
                nextToken: response.pagination?.nextToken,
            };
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
        enabled: !!user?.organizationId && enabled,
    });
};

/**
 * Hook that fetches a single page of members.
 * Each page has its own cache entry.
 */
export const useMembersPage = (params: UseMembersPageParams = {}) => {
    const options = useGetMembersPageOptions(params);
    return useQuery(options);
};

/**
 * Wrapper hook that fetches both the requested page and page 0 (for metadata).
 * React Query handles deduplication if page === 0.
 */
export const usePaginatedMembers = ({ page = 0, filter, sort, enabled = true }: UseMembersPageParams = {}) => {
    const queryClient = useQueryClient();

    // Fetch the requested page
    const pageQuery = useMembersPage({ page, filter, sort, enabled });

    // Fetch page 0 for metadata (React Query deduplicates if page === 0)
    const page0Query = useMembersPage({ page: 0, filter, sort, enabled });

    // Use metadata from page 0, fall back to current page
    const metadata = {
        totalCount: page0Query.data?.totalCount ?? pageQuery.data?.totalCount,
        totalPages: page0Query.data?.totalPages ?? pageQuery.data?.totalPages ?? 0,
        pageSize: page0Query.data?.pageSize ?? MEMBERS_PAGE_SIZE,
        nextToken: pageQuery.data?.nextToken,
    };

    // Get query options for prefetching the next page
    const nextPageOptions = useGetMembersPageOptions({ page: page + 1, filter, sort, enabled });

    // Prefetch the next page
    useEffect(() => {
        void queryClient.prefetchQuery(nextPageOptions).catch(console.error);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, page, filter, sort, queryClient]);

    const data = useMemo(() => {
        return pageQuery.data?.members ?? [];
    }, [pageQuery.data]);

    return {
        ...pageQuery,
        data,
        metadata,
        ...metadata,
    };
};

type UseMembersCursorPageParams = {
    enabled?: boolean;
    filter?: PartialMessage<ListMembersRequest_Filter>;
    sort?: PartialMessage<ListMembersRequest_Sort>;
    token?: string;
};

type MembersCursorPageData = {
    members: PlainOrganizationMember[];
    nextToken: string;
};

/**
 * Fetches a single page of members using an opaque cursor token.
 * Pair with useCursorPagination() for prev/next navigation.
 */
export const useMembersCursorPage = ({ enabled = true, filter, sort, token = "" }: UseMembersCursorPageParams = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.listMembersPaginated(user?.organizationId || "", token, filter, sort),
        queryFn: async (): Promise<MembersCursorPageData> => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const response = await api.organizationService.listMembers({
                organizationId: user?.organizationId,
                filter,
                sort,
                pagination: {
                    pageSize: MEMBERS_PAGE_SIZE,
                    token,
                },
            });

            return {
                members: response.members.map(toPlainOrganizationMember),
                nextToken: response.pagination?.nextToken ?? "",
            };
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
        enabled: !!user?.organizationId && enabled,
    });
};

export type UseListMembersInfiniteParams = {
    search?: string;
    excludeGroupIds?: string[];
    enabled?: boolean;
};

/**
 * Hook for fetching members with infinite scroll and backend search.
 * Use this for member selectors that need to scale to large organizations.
 */
export const useListMembersInfinite = ({
    search,
    excludeGroupIds,
    enabled = true,
}: UseListMembersInfiniteParams = {}) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useInfiniteQuery({
        queryKey: organizationQueryKey.listMembersInfinite(user?.organizationId || "", search, excludeGroupIds),
        queryFn: async ({ pageParam }) => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const filter: PartialMessage<ListMembersRequest_Filter> = {
                statuses: [UserStatus.ACTIVE],
            };
            if (search) {
                filter.search = search;
            }
            if (excludeGroupIds && excludeGroupIds.length > 0) {
                filter.excludeGroupIds = excludeGroupIds;
            }

            const response = await api.organizationService.listMembers({
                organizationId: user.organizationId,
                filter: Object.keys(filter).length > 0 ? filter : undefined,
                pagination: {
                    token: pageParam,
                },
            });

            return {
                members: response.members.map(toPlainOrganizationMember),
                pagination: response.pagination,
            };
        },
        initialPageParam: "",
        getNextPageParam: (lastPage) => {
            if (!lastPage.pagination?.nextToken) {
                return undefined;
            }
            return lastPage.pagination.nextToken;
        },
        enabled: enabled && !!user?.organizationId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        placeholderData: keepPreviousData,
    });
};

/**
 * Returns the shared DataLoader singleton for member lookups.
 *
 * All components share the same loader instance (per org), which is
 * what enables cross-component batching.
 */
const useMemberLoader = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    return getMemberLoader(api, user?.organizationId || "");
};

/**
 * Get a member from the query cache by user ID.
 * Returns undefined if not in cache.
 */
export const getMemberFromCache = (queryClient: QueryClient, userId: string): PlainOrganizationMember | undefined => {
    return queryClient.getQueryData<PlainOrganizationMember>(organizationQueryKey.member(userId));
};

const MEMBER_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Hook that fetches a single member by user ID.
 *
 * Uses a DataLoader under the hood: multiple useMemberQuery hooks mounting
 * in the same render cycle share a single batched API call.
 * Each member gets its own query observer, so invalidation triggers a refetch.
 */
export const useMemberQuery = (userId: string, { enabled = true }: { enabled?: boolean } = {}) => {
    const loader = useMemberLoader();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.member(userId),
        queryFn: () => loader.load(userId),
        enabled: enabled && !!userId && !!user?.organizationId,
        staleTime: MEMBER_STALE_TIME,
        retry: defaultRetry,
    });
};

/**
 * Hook that prefetches members by their user IDs and populates individual caches.
 *
 * Each ID gets its own query observer backed by a DataLoader that batches
 * concurrent fetches into bulk API calls. Invalidating ['member', userId]
 * triggers a refetch for that specific member.
 */
export const usePrefetchMembersByIds = (userIds: string[], { enabled = true }: { enabled?: boolean } = {}) => {
    const loader = useMemberLoader();
    const { data: user } = useAuthenticatedUser();

    const dedupedIds = useMemo(() => [...new Set(userIds.filter(Boolean))], [userIds]);

    return useQueries({
        queries: dedupedIds.map((id) => ({
            queryKey: organizationQueryKey.member(id),
            queryFn: () => loader.load(id),
            enabled: enabled && !!id && !!user?.organizationId,
            staleTime: MEMBER_STALE_TIME,
            retry: defaultRetry,
        })),
        combine: (results) => ({
            isLoading: results.some((r) => r.isLoading),
            isPending: results.some((r) => r.isPending),
            isError: results.some((r) => r.isError),
        }),
    });
};

/**
 * Hook that fetches members by their user IDs and returns them as a Map.
 *
 * Each member ID gets a real query observer, so invalidating ['member', userId]
 * triggers a refetch. Concurrent fetches are batched via DataLoader.
 *
 * Use this when you only need a known set of members by their user IDs.
 */
export const useMembersByIds = (userIds: string[], { enabled = true }: { enabled?: boolean } = {}) => {
    const loader = useMemberLoader();
    const { data: user } = useAuthenticatedUser();

    const dedupedIds = useMemo(() => [...new Set(userIds.filter(Boolean))], [userIds]);

    const results = useQueries({
        queries: dedupedIds.map((id) => ({
            queryKey: organizationQueryKey.member(id),
            queryFn: () => loader.load(id),
            enabled: enabled && !!id && !!user?.organizationId,
            staleTime: MEMBER_STALE_TIME,
            retry: defaultRetry,
        })),
        combine: (queryResults) => {
            const members = new Map<string, PlainOrganizationMember>();
            for (let i = 0; i < queryResults.length; i++) {
                const member = queryResults[i].data;
                if (member) {
                    members.set(dedupedIds[i], member);
                }
            }
            return {
                members,
                isLoading: queryResults.some((r) => r.isLoading),
                isPending: queryResults.some((r) => r.isPending),
                isError: queryResults.some((r) => r.isError),
            };
        },
    });

    return results;
};

export const useDeleteOrganization = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const organizationId = user.organizationId;
            await api.organizationService.deleteOrganization({
                organizationId,
            });
            return;
        },
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: getAccountQueryKey() });
            await client.invalidateQueries({
                queryKey: organizationQueryKey.get(user?.organizationId),
            });
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listMembers(user?.organizationId || ""),
            });
        },
    });
};

export const useSetRole = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ userID, role }: { userID: string; role: OrganizationRole }) => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const organizationId = user.organizationId;
            await api.organizationService.setRole({
                organizationId,
                userId: userID,
                role,
            });

            return {};
        },
        onSuccess: async (_, { userID }) => {
            if (!user) {
                return;
            }

            // Invalidate the individual member cache so DataLoader-backed observers refetch
            await client.invalidateQueries({
                queryKey: organizationQueryKey.member(userID),
            });
            // Invalidate all listMembers queries (with any filter/sort)
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listMembers(user.organizationId || "").slice(0, -1),
            });
            // Invalidate all listMembersPaginated queries (with any filter/sort/page)
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listMembersPaginated(user.organizationId || "").slice(0, -1),
            });
        },
        onError: (error) => {
            console.error(error);
        },
    });
};

export function roleName(role: OrganizationRole) {
    switch (role) {
        case OrganizationRole.ADMIN:
            return "Admin";
        case OrganizationRole.MEMBER:
            return "Member";
        case OrganizationRole.UNSPECIFIED:
            return "Unspecified";
    }
}

export const useOrganizationInvite = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.orgInvite(user?.organizationId || ""),
        queryFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const { invite } = await api.organizationService.getOrganizationInvite({
                organizationId: user?.organizationId,
            });

            return invite;
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCreateOrganizationInvite = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const organizationId = user.organizationId;
            const resp = await api.organizationService.createOrganizationInvite({
                organizationId,
            });

            return resp.invite;
        },
        onSuccess: async () => {
            if (!user) {
                return;
            }

            await client.invalidateQueries({
                queryKey: organizationQueryKey.orgInvite(user?.organizationId || ""),
            });
        },
    });
};

export const useJoinOrganizationWithInvite = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ inviteID }: { inviteID: string }) => {
            const { member } = await api.organizationService.joinOrganization({
                joinId: {
                    value: inviteID,
                    case: "inviteId",
                },
            });

            if (!member) {
                throw new Error("Failed to join organization");
            }

            return member;
        },
        onSuccess: async () => {
            await client.invalidateQueries({ queryKey: getAccountQueryKey() });
        },
    });
};

export const useListSSOConfigurations = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.listSSOConfigurations(user?.organizationId || ""),
        queryFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const ssoConfigurations = await autoPaginate(
                (params) => api.organizationService.listSSOConfigurations(params),
                {
                    organizationId: user?.organizationId,
                },
                (response) => response.ssoConfigurations,
            );

            return ssoConfigurations;
        },
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useCreateSSOConfiguration = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            clientId,
            clientSecret,
            emailDomain,
            issuerUrl,
            displayName,
            additionalScopes,
        }: CreateSSOConfigurationRequest) => {
            const { ssoConfiguration } = await api.organizationService.createSSOConfiguration(
                new CreateSSOConfigurationRequest({
                    organizationId,
                    clientId,
                    clientSecret,
                    emailDomain,
                    issuerUrl,
                    displayName,
                    additionalScopes,
                }),
            );

            if (!ssoConfiguration) {
                throw new Error("Failed to create SSO configuration");
            }

            return ssoConfiguration;
        },
        onSuccess: async (data) => {
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listSSOConfigurations(data.organizationId || ""),
            });
        },
    });
};

export const useDeleteSSOConfiguration = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (ssoConfigurationId: string) => {
            await api.organizationService.deleteSSOConfiguration(
                new DeleteSSOConfigurationRequest({
                    ssoConfigurationId,
                }),
            );
        },
        onSuccess: async () => {
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listSSOConfigurations(user?.organizationId || ""),
            });
        },
    });
};

export const useUpdateSSOConfiguration = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({
            ssoConfigurationId,
            clientId,
            clientSecret,
            issuerUrl,
            claims,
            state,
            emailDomain,
            emailDomains,
            displayName,
            additionalScopes,
        }: PartialMessage<UpdateSSOConfigurationRequest>) => {
            await api.organizationService.updateSSOConfiguration(
                new UpdateSSOConfigurationRequest({
                    ssoConfigurationId,
                    clientId,
                    clientSecret,
                    issuerUrl,
                    claims,
                    state,
                    emailDomain,
                    emailDomains,
                    displayName,
                    additionalScopes,
                }),
            );
        },
        onSuccess: async () => {
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listSSOConfigurations(user?.organizationId || ""),
            });
        },
    });
};

export const useGetDomainVerification = (domain?: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const query = useQuery({
        queryKey: organizationQueryKey.getDomainVerification(user?.organizationId || "", domain),
        queryFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            if (!domain) {
                return {};
            }

            const domainVerifications = await autoPaginate(
                (params) => api.organizationService.listDomainVerifications(params),
                { organizationId: user?.organizationId },
                (response) => response.domainVerifications,
            );

            if (domain) {
                const domainVerification = domainVerifications.find((v) => v.domain === domain);
                if (domainVerification) {
                    return { domainVerification };
                }
            }

            return {};
        },
        enabled: !!domain,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });

    return { ...query, domainVerification: query.data?.domainVerification };
};

export const useGetOrCreateDomainVerification = (domain: string | undefined) => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    const createMutation = useMutation({
        mutationFn: async (domain: string) => {
            const { domainVerification } = await api.organizationService.createDomainVerification({
                organizationId: user?.organizationId,
                domain,
            });
            if (!domainVerification) {
                throw new Error("Failed to update domain verification");
            }
            return domainVerification;
        },
        onSuccess: async (data) => {
            client.setQueryData(organizationQueryKey.getDomainVerification(user?.organizationId || "", data?.domain), {
                domainVerification: data,
            });
            // Invalidate the list query so the domain list updates
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listDomainVerifications(user?.organizationId || ""),
            });
        },
    });

    const { domainVerification, isPending, error, isLoading } = useGetDomainVerification(domain);

    // If the domain verification is not found, create it
    // For deduplication of events.
    const calledOnce = useRef(false);
    useEffect(() => {
        if (
            domain &&
            !isLoading &&
            !isPending &&
            !domainVerification &&
            !createMutation.isError &&
            !createMutation.isPending
        ) {
            if (calledOnce.current) {
                return;
            }
            calledOnce.current = true;
            createMutation.mutate(domain);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domainVerification, domain, isLoading, isPending]);

    return {
        domainVerification: domainVerification || createMutation.data,
        isPending: isPending || createMutation.isPending,
        isLoading: isLoading || createMutation.isPending,
        error: error || createMutation.error,
    };
};

export type PlainDomainVerification = PlainMessage<DomainVerification>;

export const useVerifyDomain = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();
    return useMutation({
        mutationFn: async (params: DomainVerification | PlainDomainVerification) => {
            const { domainVerification: updatedDomainVerification } = await api.organizationService.verifyDomain({
                domainVerificationId: params.id,
            });

            return updatedDomainVerification;
        },
        onSuccess: async (data?: DomainVerification) => {
            await client.invalidateQueries({
                queryKey: organizationQueryKey.getDomainVerification(user?.organizationId || "", data?.domain),
            });
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listDomainVerifications(user?.organizationId || ""),
            });
        },
    });
};

export const useDeleteDomainVerification = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (domainVerificationId: string) => {
            await api.organizationService.deleteDomainVerification(
                new DeleteDomainVerificationRequest({
                    domainVerificationId,
                }),
            );
        },
        onSuccess: async () => {
            await client.invalidateQueries({
                queryKey: organizationQueryKey.listDomainVerifications(user?.organizationId || ""),
            });
        },
    });
};

export const useListDomainVerifications = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.listDomainVerifications(user?.organizationId || ""),
        queryFn: async () => {
            if (!user) {
                throw new Error("Not authenticated");
            }

            const domainVerifications = await autoPaginate(
                (params) => api.organizationService.listDomainVerifications(params),
                { organizationId: user?.organizationId },
                (response) => response.domainVerifications,
            );

            return domainVerifications.map((v) => toPlainMessage(v));
        },
        enabled: !!user?.organizationId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type PlainOrganizationPolicies = PlainMessage<OrganizationPolicies>;

export const toPlainOrganizationPolicies = (op: OrganizationPolicies): PlainOrganizationPolicies => {
    return toPlainMessage(op);
};

export const useGetOrganizationPolicies = ({ enabled }: { enabled: boolean } = { enabled: true }) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    // This allows the hook to immediately use cached data
    return useQuery({
        queryKey: organizationQueryKey.getOrganizationPolicies(user?.organizationId || ""),
        queryFn: async () => {
            const { policies } = await api.organizationService.getOrganizationPolicies({
                organizationId: user?.organizationId,
            });

            if (!policies) {
                throw new Error("Failed to get organization policies");
            }

            return toPlainOrganizationPolicies(policies);
        },
        enabled: !!user?.organizationId && enabled,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useUpdateOrganizationPolicies = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (policies: PartialMessage<OrganizationPolicies>) => {
            return await api.organizationService.updateOrganizationPolicies({
                organizationId: user?.organizationId,
                ...policies,
            });
        },
        onMutate: async (policies) => {
            if (!user?.organizationId) return;

            // Cancel any outgoing refetches to avoid them overwriting our optimistic update
            await client.cancelQueries({
                queryKey: organizationQueryKey.getOrganizationPolicies(user.organizationId),
            });

            // Snapshot the previous value
            const previousPolicies = client.getQueryData<PlainOrganizationPolicies>(
                organizationQueryKey.getOrganizationPolicies(user.organizationId),
            );

            // Optimistically update to the new value
            client.setQueryData(
                organizationQueryKey.getOrganizationPolicies(user.organizationId),
                (old?: PlainOrganizationPolicies) => ({
                    ...old,
                    ...policies,
                }),
            );

            // Return a context object with the previous value
            return { previousPolicies };
        },
        onError: (_error, _variables, context) => {
            // If the mutation fails, use the context returned from onMutate to roll back
            if (context?.previousPolicies && user?.organizationId) {
                client.setQueryData(
                    organizationQueryKey.getOrganizationPolicies(user.organizationId),
                    context.previousPolicies,
                );
            }
        },
        onSettled: async () => {
            // Always refetch after error or success to ensure our local data is correct
            if (user?.organizationId) {
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.getOrganizationPolicies(user.organizationId),
                });
            }
        },
    });
};

export const handleOrganizationPolicyEvent = async (
    api: GitpodAPI,
    client: QueryClient,
    evt: WatchEventsResponse,
    organizationId: string,
) => {
    if (evt.resourceType === ResourceType.ORGANIZATION_POLICY) {
        if (evt.operation === ResourceOperation.UPDATE) {
            const policies = await refetchOrganizationPolicies(api, organizationId);
            setOrganizationPoliciesInCache(
                client,
                organizationId,
                policies ? toPlainOrganizationPolicies(policies) : undefined,
            );

            // Invalidate all editor list caches when policies change
            // since editor permissions are affected by organization policies.
            await client.invalidateQueries({
                queryKey: editorQueryKey.listAll(),
            });
        }
    }
};

const refetchOrganizationPolicies = async (
    api: GitpodAPI,
    organizationId: string,
): Promise<OrganizationPolicies | undefined> => {
    try {
        const { policies } = await api.organizationService.getOrganizationPolicies({
            organizationId,
        });
        return policies;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        if (!isNotFound) {
            console.error("Failed to refetch organization policies", organizationId);
        }
    }
};

function setOrganizationPoliciesInCache(
    client: QueryClient,
    organizationId: string,
    policies?: PlainOrganizationPolicies,
) {
    client.setQueryData(organizationQueryKey.getOrganizationPolicies(organizationId), policies);
}

export type PlainSetupBillingResponse = PlainMessage<SetupBillingResponse>;
export type PlainCompleteBillingSetupResponse = PlainMessage<CompleteBillingSetupResponse>;

export const useSetupBilling = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (): Promise<PlainSetupBillingResponse> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.setupBilling({
                organizationId: user.organizationId,
            });

            return toPlainMessage(response);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to setup billing:", error);
        },
    });
};

export const useCompleteBillingSetup = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (setupIntentId: string): Promise<PlainCompleteBillingSetupResponse> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.completeBillingSetup({
                organizationId: user.organizationId,
                setupIntentId,
            });

            return toPlainMessage(response);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to complete billing setup:", error);
        },
    });
};

export type PlainGetBillingInfoResponse = PlainMessage<GetBillingInfoResponse>;

export const useGetBillingInfo = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.getBillingInfo(user?.organizationId || ""),
        queryFn: async (): Promise<PlainGetBillingInfoResponse> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            try {
                const response = await api.billingService.getBillingInfo({
                    organizationId: user.organizationId,
                });

                return toPlainMessage(response);
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.NotFound) {
                    return toPlainMessage(new GetBillingInfoResponse({}));
                }
                throw error;
            }
        },
        enabled: !!user?.organizationId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

/**
 * Mutation hook to trigger billing reconciliation.
 * Call this when the user navigates to the billing page to ensure
 * billing state is up-to-date with external providers.
 */
export const useReconcileBilling = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            await api.billingService.reconcileBilling({
                organizationId: user.organizationId,
            });
        },
        onSuccess: () => {
            // Invalidate billing info to refetch with reconciled data
            if (user?.organizationId) {
                void client.invalidateQueries({
                    queryKey: organizationQueryKey.getBillingInfo(user.organizationId),
                });
            }
        },
        throwOnError: false, // Don't fail the page if reconciliation fails
        retry: false,
    });
};

export const useReloadBillingInfo = () => {
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    const reload = useCallback(
        (cancelFetchingQueries = true) => {
            if (!user?.organizationId) {
                return;
            }
            if (client.isFetching({ queryKey: organizationQueryKey.getBillingInfo(user.organizationId) })) {
                if (cancelFetchingQueries) {
                    client
                        .cancelQueries({ queryKey: organizationQueryKey.getBillingInfo(user.organizationId) })
                        .catch(console.error);
                } else {
                    // No need to pile up refetch requests
                    return;
                }
            }
            client
                .refetchQueries({ queryKey: organizationQueryKey.getBillingInfo(user.organizationId) })
                .catch(console.error);
        },
        [client, user?.organizationId],
    );

    return {
        reload,
    };
};

export type PlainGetStripePortalUrlResponse = PlainMessage<GetStripePortalUrlResponse>;

export const useGetStripePortalUrl = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (): Promise<PlainGetStripePortalUrlResponse> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.getStripePortalUrl({
                organizationId: user.organizationId,
            });

            return toPlainMessage(response);
        },
        onError: (error) => {
            console.error("Failed to get Stripe portal URL:", error);
        },
    });
};

export type PlainCancelSubscriptionResponse = PlainMessage<CancelSubscriptionResponse>;

export const useCancelSubscription = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (subscriptionType: SubscriptionType): Promise<PlainCancelSubscriptionResponse> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.cancelSubscription({
                organizationId: user.organizationId,
                subscriptionType,
            });

            return toPlainMessage(response);
        },
        onSuccess: async () => {
            // Invalidate billing info to refresh the UI
            if (user?.organizationId) {
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.getBillingInfo(user.organizationId),
                });
                // Also invalidate organization data to refresh tier info
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.get(user.organizationId),
                });
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.listSubscriptions(user.organizationId),
                });
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to cancel subscription:", error);
        },
    });
};

export const useStripePublishableKey = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: keyWithPrincipal(["billing", "stripe-publishable-key", { organizationId: user?.organizationId }]),
        queryFn: async () => {
            if (!user?.organizationId) {
                throw new Error("No organization found");
            }

            const response = await api.billingService.getStripePublishableKey({
                organizationId: user.organizationId,
            });

            return response.publishableKey;
        },
        enabled: !!user?.organizationId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useSetBillingAddress = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ address, name }: { address: BillingAddress; name: string }) => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.setBillingAddress({
                organizationId: user.organizationId,
                address,
                name,
            });

            return toPlainMessage(response);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to create core subscription:", error);
        },
    });
};

export const useCreateCoreSubscription = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();
    const setTaxID = useSetTaxID();

    return useMutation({
        mutationFn: async (options?: {
            amountUsd?: number;
            couponCode?: string;
            businessData?: {
                isPurchasingAsBusiness: boolean;
                taxIdTypeId: string;
                taxIdValue: string;
            };
        }) => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            // Handle business tax ID setup if provided
            if (options?.businessData?.isPurchasingAsBusiness) {
                const { taxIdTypeId, taxIdValue } = options.businessData;

                // Basic validation
                if (!taxIdTypeId) {
                    throw new Error("Please select a tax ID type");
                }
                if (!taxIdValue.trim()) {
                    throw new Error("Please enter a tax ID value");
                }

                // Get the actual Stripe tax ID code from the selected ID
                const selectedTaxType = TAX_ID_TYPES_BY_ID[taxIdTypeId];
                if (!selectedTaxType) {
                    throw new Error("Invalid tax ID type selected");
                }

                try {
                    await setTaxID.mutateAsync({
                        taxIdType: selectedTaxType.code,
                        taxIdValue,
                    });
                } catch (taxError) {
                    // Handle tax ID validation errors
                    const errorMsg = taxError instanceof Error ? taxError.message : "Failed to save tax information";
                    throw new Error(errorMsg);
                }
            }

            const amountUsd = options?.amountUsd ?? 20;
            const amountCents = BigInt(amountUsd * 100);

            const response = await api.billingService.createSubscription({
                organizationId: user.organizationId,
                subscriptionType: SubscriptionType.CORE,
                amountCents,
                couponCode: options?.couponCode || "",
            });

            return toPlainMessage(response);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to create core subscription:", error);
        },
        onSuccess: async () => {
            // Invalidate billing info to refresh the UI
            if (user?.organizationId) {
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.getBillingInfo(user.organizationId),
                });
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.listSubscriptions(user.organizationId),
                });
                // Also invalidate organization data to refresh tier info
                await client.invalidateQueries({
                    queryKey: organizationQueryKey.get(user.organizationId),
                });
            }
        },
        meta: {
            method: "BillingService.CreateSubscription",
        },
    });
};

export const useListSubscriptions = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.listSubscriptions(user?.organizationId || ""),
        queryFn: async () => {
            const response = await api.billingService.listSubscriptions({
                organizationId: user?.organizationId,
            });

            const plainSubscriptions = response.subscriptions.map((s) => toPlainMessage(s));
            return plainSubscriptions;
        },
        enabled: !!user?.organizationId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGrantCredits = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (creditsAmount: number) => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.grantCredits({
                organizationId: user.organizationId,
                creditsAmount,
            });

            return toPlainMessage(response);
        },
        onSuccess: async () => {
            if (!user?.organizationId) {
                return;
            }
            // Invalidate billing info to refresh credit balance
            await client.invalidateQueries({
                queryKey: organizationQueryKey.getBillingInfo(user.organizationId),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to grant credits:", error);
        },
    });
};

/**
 * Custom hook for confirming Stripe payments
 * This should be used within a Stripe Elements context
 */
export const useConfirmStripePayment = () => {
    const stripe = useStripe();
    const elements = useElements();

    return useMutation({
        mutationFn: async (): Promise<PaymentIntent> => {
            if (!stripe || !elements) {
                throw new Error("Stripe not initialized");
            }

            await elements.submit();

            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
                confirmParams: {
                    return_url: window.location.href,
                },
            });

            if (error) {
                throw new Error(error.message || "Payment confirmation failed");
            }

            if (!paymentIntent) {
                throw new Error("No payment intent returned");
            }

            return paymentIntent;
        },
        onError: (error) => {
            console.error("Payment confirmation error:", error);
        },
    });
};

export const useSetTaxID = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ taxIdType, taxIdValue }: { taxIdType: string; taxIdValue: string }) => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.setTaxID({
                organizationId: user.organizationId,
                taxIdType,
                taxIdValue,
            });

            return toPlainMessage(response);
        },

        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to set tax ID:", error);
        },
    });
};

export type PlainGetCouponResponse = PlainMessage<GetCouponResponse>;

export const useCheckCouponCode = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (couponCode: string): Promise<PlainGetCouponResponse | "invalid"> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            try {
                const response = await api.billingService.getCoupon({
                    couponCode,
                    organizationId: user.organizationId,
                });

                return toPlainMessage(response);
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.NotFound) {
                    return "invalid";
                }
                throw error;
            }
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        onError: (error) => {
            console.error("Failed to check coupon code:", error);
        },
    });
};

// Custom Domain Queries

export const useGetCustomDomain = (organizationId: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: organizationQueryKey.getCustomDomain(organizationId),
        queryFn: async () => {
            try {
                const response = await api.organizationService.getCustomDomain({
                    organizationId,
                });
                return response.customDomain;
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.NotFound) {
                    return undefined;
                }
                throw error;
            }
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useSetCustomDomain = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            organizationId,
            domainName,
            provider,
            cloudAccountId,
        }: {
            organizationId: string;
            domainName: string;
            provider: CustomDomainProvider;
            cloudAccountId: string;
        }) => {
            // Check if domain exists first
            try {
                await api.organizationService.getCustomDomain({ organizationId });
                // Domain exists, update it
                const response = await api.organizationService.updateCustomDomain({
                    organizationId,
                    domainName,
                    cloudAccountId,
                    provider,
                });
                return response.customDomain;
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.NotFound) {
                    // Domain doesn't exist, create it
                    const response = await api.organizationService.createCustomDomain({
                        organizationId,
                        domainName,
                        cloudAccountId,
                        provider,
                    });
                    return response.customDomain;
                }
                throw error;
            }
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKey.getCustomDomain(variables.organizationId),
            });
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKey.getEnforceDomainPolicy(variables.organizationId),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useDeleteCustomDomain = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ organizationId }: { organizationId: string }) => {
            await api.organizationService.deleteCustomDomain({
                organizationId,
            });
        },
        onSuccess: (_, variables) => {
            // Explicitly set the query data to null to reflect deletion immediately
            // Note: Setting to undefined doesn't trigger an update in React Query,
            // so we use null instead (similar to project deletion pattern)
            queryClient.setQueryData<CustomDomain | undefined>(
                organizationQueryKey.getCustomDomain(variables.organizationId),
                null as unknown as undefined,
            );
            // Invalidate enforcement policy query
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKey.getEnforceDomainPolicy(variables.organizationId),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useGetEnforceDomainPolicy = (organizationId: string) => {
    const api = useGitpodAPI();

    return useQuery({
        queryKey: organizationQueryKey.getEnforceDomainPolicy(organizationId),
        queryFn: async () => {
            const response = await api.organizationService.getOrganizationPolicies({
                organizationId,
            });
            return { enforced: response.policies?.requireCustomDomainAccess ?? false };
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useSetEnforceDomainPolicy = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ organizationId, enforced }: { organizationId: string; enforced: boolean }) => {
            await api.organizationService.updateOrganizationPolicies({
                organizationId,
                requireCustomDomainAccess: enforced,
            });
        },
        onMutate: async ({ organizationId, enforced }) => {
            // Cancel outgoing refetches
            await queryClient.cancelQueries({
                queryKey: organizationQueryKey.getEnforceDomainPolicy(organizationId),
            });

            // Snapshot previous value
            const previousPolicy = queryClient.getQueryData(
                organizationQueryKey.getEnforceDomainPolicy(organizationId),
            );

            // Optimistically update
            queryClient.setQueryData(organizationQueryKey.getEnforceDomainPolicy(organizationId), { enforced });

            // Return context with snapshot
            return { previousPolicy };
        },
        onError: (_err, variables, context) => {
            // Revert on error
            if (context?.previousPolicy) {
                queryClient.setQueryData(
                    organizationQueryKey.getEnforceDomainPolicy(variables.organizationId),
                    context.previousPolicy,
                );
            }
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKey.getEnforceDomainPolicy(variables.organizationId),
            });
            // Also invalidate the organization policies cache
            void queryClient.invalidateQueries({
                queryKey: organizationQueryKey.getOrganizationPolicies(variables.organizationId),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useClearTopupFailure = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async () => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.clearTopupFailure({
                organizationId: user.organizationId,
            });

            return toPlainMessage(response);
        },
        onSuccess: async () => {
            if (!user?.organizationId) {
                return;
            }
            // Invalidate billing info to clear the topup failure from UI
            await client.invalidateQueries({
                queryKey: organizationQueryKey.getBillingInfo(user.organizationId),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type PlainAnnouncementBanner = {
    organizationId: string;
    message: string;
    enabled: boolean;
};

const DEFAULT_ANNOUNCEMENT_BANNER: PlainAnnouncementBanner = {
    organizationId: "",
    message: "",
    enabled: false,
};

export const useGetAnnouncementBanner = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.getAnnouncementBanner(user?.organizationId || ""),
        queryFn: async (): Promise<PlainAnnouncementBanner> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated");
            }

            const { banner } = await api.organizationService.getAnnouncementBanner({
                organizationId: user.organizationId,
            });

            if (banner) {
                return toPlainMessage(banner);
            }

            return { ...DEFAULT_ANNOUNCEMENT_BANNER, organizationId: user.organizationId };
        },
        enabled: !!user?.organizationId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useUpdateAnnouncementBanner = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ message, enabled }: { message?: string; enabled?: boolean }) => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated");
            }

            const { banner } = await api.organizationService.updateAnnouncementBanner({
                organizationId: user.organizationId,
                message,
                enabled,
            });

            return banner ? toPlainMessage(banner) : undefined;
        },
        onSuccess: async () => {
            if (!user?.organizationId) {
                return;
            }

            await client.invalidateQueries({
                queryKey: organizationQueryKey.getAnnouncementBanner(user.organizationId),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export type PlainAutoTopupSettings = PlainMessage<AutoTopupSettings>;

const DEFAULT_AUTO_TOPUP_SETTINGS: PlainAutoTopupSettings = {
    enabled: false,
    amountCredits: 100,
    maxDailyTopups: 0,
};

export const useGetAutoTopupSettings = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: organizationQueryKey.getAutoTopupSettings(user?.organizationId || ""),
        queryFn: async (): Promise<PlainAutoTopupSettings> => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            try {
                const response = await api.billingService.getAutoTopupSettings({
                    organizationId: user.organizationId,
                });

                return response.settings ? toPlainMessage(response.settings) : DEFAULT_AUTO_TOPUP_SETTINGS;
            } catch (error) {
                if (error instanceof ConnectError && error.code === Code.NotFound) {
                    return DEFAULT_AUTO_TOPUP_SETTINGS;
                }
                throw error;
            }
        },
        enabled: !!user?.organizationId,
        staleTime: 60_000,
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};

export const useUpdateAutoTopupSettings = () => {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (settings: PlainAutoTopupSettings) => {
            if (!user?.organizationId) {
                throw new Error("Not authenticated or no organization");
            }

            const response = await api.billingService.updateAutoTopupSettings({
                organizationId: user.organizationId,
                settings,
            });

            return response.settings ? toPlainMessage(response.settings) : settings;
        },
        onSuccess: async (data) => {
            if (!user?.organizationId) {
                return;
            }
            client.setQueryData(organizationQueryKey.getAutoTopupSettings(user.organizationId), data);
            await client.invalidateQueries({
                queryKey: organizationQueryKey.getBillingInfo(user.organizationId),
            });
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
    });
};
